import { AmplifyUser } from "@aws-amplify/ui";
import { Button, Drawer, List, ListItem, ListItemText } from "@mui/material";
import { styled } from "@mui/system";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import "./index.css"; // Updated import statement for custom table styles
import "./HibiyaCafe.css";

const APP_API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";

interface Employee {
  id: number;
  employee_name: string;
  employee_id: number;
  employee_department: string;
  employee_base_office: string;
  employee_current_office: string;
  employee_area: string;
}

const Root = styled("div")`
  display: flex;
`;

const Content = styled("div")`
  flex-grow: 1;
  padding: 20px;
  margin-left: 240px;
`;

const CenteredContainer = styled("div")`
  width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled("div")`
  flex-grow: 1;
`;

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 240px;
    top: 64px;
    height: calc(100% - 64px);
  }
`;

const locationMapping: { [key: string]: string } = {
  Hibiya: "日比谷",
  Osaka: "大阪",
  Sapporo: "札幌",
  Fukuoka: "福岡",
};

function CustomTable(props: { user: AmplifyUser | undefined }) {
  const [employeeData, setEmployeeData] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [lasLoadedAt, setLastLoadedAt] = useState<Date | null>(null); 
  const [selectedLocation, setSelectedLocation] = useState<string>("Hibiya");

  const columns: GridColDef[] = [
    { field: "employee_name", headerName: "Employee Name", flex: 1 },
    { field: "employee_base_office", headerName: "Base Office", flex: 1 },
    { field: "employee_department", headerName: "Department", flex: 2 },
    { field: "employee_area", headerName: "Current Area", flex: 1 },
  ];

  useEffect(() => {
    fetchEmployeeData();
  }, [props.user]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken();
      const jwtToken = idToken.getJwtToken();

      const response = await fetch(`${APP_API_ENDPOINT}/Employees`, {
        method: "GET",
        headers: {
          Authorization: jwtToken,
        },
      });

      const data = await response.json();

      if (Array.isArray(data)) {
        const dataWithId = data.map((employee, index) => ({
          ...employee,
          id: index + 1,
          employee_name: employee.user_name,
          employee_id: employee.user_id,
          employee_department:
            employee.Department === "<empty>" ? "-" : employee.Department,
          employee_base_office:
            employee.baseOffice === "<empty>" ? "-" : employee.baseOffice,
          employee_current_office:
            employee.currentOffice === "<empty>" ? "-" : employee.currentOffice,
          employee_area: employee.area,
        }));
        setEmployeeData(dataWithId);
      }

      setLastLoadedAt(new Date());
    } catch (error) {
      setError("Error fetching employee data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputQuery = event.target.value;
    setSearchQuery(inputQuery);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const filteredEmployeeData = employeeData.filter((employee) => {
    const query = searchQuery.toLowerCase().trim();
    const employeeName = employee.employee_name
      ? employee.employee_name.toLowerCase()
      : "";
    const employeeCurrentOffice = employee.employee_current_office
      ? employee.employee_current_office.toLowerCase()
      : "";
    const employeeDepartment = employee.employee_department
      ? employee.employee_department.toLowerCase()
      : "";
    const employeeArea = employee.employee_area
      ? employee.employee_area.toLowerCase()
      : "";
    const employeeCurrentOfficeMapped = employee.employee_current_office
      ? employee.employee_current_office.trim()
      : "";
    const mappedSelectedLocation = locationMapping[selectedLocation];

    const matchesSearchQuery =
      employeeName.includes(query) ||
      employeeCurrentOffice.includes(query) ||
      employeeDepartment.includes(query) ||
      employeeArea.includes(query);

    const matchesSelectedLocation =
      employeeCurrentOfficeMapped === mappedSelectedLocation;

    return matchesSearchQuery && matchesSelectedLocation;
  });

  const totalEmployeesForSelectedLocation = employeeData.filter((employee) => {
    const employeeCurrentOfficeMapped = employee.employee_current_office
      ? employee.employee_current_office.trim()
      : "";
    const mappedSelectedLocation = locationMapping[selectedLocation];
    return employeeCurrentOfficeMapped === mappedSelectedLocation;
  }).length;

  return (
    <Root>
      <StyledDrawer variant="permanent" anchor="left">
        <List>
          {["Hibiya", "Osaka", "Sapporo", "Fukuoka"].map((location) => (
            <ListItem
              button
              key={location}
              selected={selectedLocation === location}
              onClick={() => setSelectedLocation(location)}
            >
              <ListItemText primary={location} />
            </ListItem>
          ))}
        </List>
      </StyledDrawer>

      <Content>
        <CenteredContainer>
          <MainContent>
            <div
              className="search-container"
              style={{ display: "flex", alignItems: "center" }}
            >
              <input
                type="text"
                placeholder="Search by employee name, current office, department, or current area..."
                className="search-input"
                value={searchQuery}
                onChange={handleSearch}
              />
              <Button
                variant="outlined"
                onClick={fetchEmployeeData}
                style={{
                  marginLeft: "10px",
                  width: "150px",
                }}
              >
                Reload
              </Button>
            </div>

            {/* Set container height from 550->500 for more visible footer */}
            <div style={{ height: 500, width: "100%" }}>
              {filteredEmployeeData.length === 0 ? (
                <div>User not found</div>
              ) : (
                <DataGrid
                  rowHeight={35}
                  rows={filteredEmployeeData}
                  columns={columns}
                  hideFooter
                  // Use normal spacing between items in the component
                  density="standard"
                  // Automatically adjust height if there are fewer than 14 employees
                  autoHeight={filteredEmployeeData.length < 14}
                />
              )}
            </div>
          </MainContent>

          {/* Footer */}
          <div style={{ marginBottom: "130px" }}>
            Displaying {filteredEmployeeData.length} of{" "}
            {totalEmployeesForSelectedLocation}
            <div style={{fontSize: "0.8em", color: "#888"}}>
            {lasLoadedAt && (
              <div>Last loaded: {lasLoadedAt.toLocaleString()}</div>
            )}
              </div>
          </div>
        </CenteredContainer>
      </Content>
    </Root>
  );
}

export default CustomTable;